'use client';

import React, { useState, useEffect } from 'react';
import { HeartIcon } from '@heroicons/react/24/solid';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Sponsor {
  id: string;
  sponsor_name: string;
  short_description: string;
  contact_email: string;
  contact_phone: string;
  photo_url: string;
}

export default function ThankYouSponsors() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('sponsors')
          .select('*')
          .eq('sponsor_type', 'thankyou')
          .order('sort_order', { ascending: true });
        if (!error && data) setSponsors(data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchSponsors();
  }, []);

  if (loading) {
    return (
      <section>
        <div className="flex items-center gap-3 mb-4">
          <div className="gradient-accent rounded-xl p-2.5">
            <HeartIcon className="w-5 h-5 text-accent-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Thank You Sponsors</h2>
            <p className="text-xs text-muted-foreground">Supporting New Milton RFC</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-2xl p-4 border border-border h-28 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="gradient-accent rounded-xl p-2.5">
          <HeartIcon className="w-5 h-5 text-accent-foreground" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Thank You Sponsors</h2>
          <p className="text-xs text-muted-foreground">Supporting New Milton RFC</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {sponsors?.map((s) => (
          <div key={s?.id} className="rounded-2xl p-4 card-shadow border border-border bg-card text-center">
            <div className="w-14 h-14 rounded-xl mx-auto mb-3 flex items-center justify-center bg-muted border border-border overflow-hidden">
              {s?.photo_url ? (
                <Image src={s.photo_url} alt={`${s.sponsor_name} logo`} width={56} height={56} className="object-contain w-full h-full" />
              ) : (
                <span className="text-2xl">🏆</span>
              )}
            </div>
            <p className="text-xs font-bold leading-tight text-foreground">{s?.sponsor_name}</p>
            {s?.short_description && (
              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{s.short_description}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}